import React, { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, HStack } from '@cad-arc';
import { useBoloIdFromNavigation } from '../../use-bolo-id-from-navigation';
import { useBoloPanelMetadata } from '../../../bolos/bolo-panel/state/bolo-panel-metadata-context';
import { AttachmentsView, AttachmentsViewHandle } from '../../../attachments/attachments-view';
import { FR_BOLO_ROOT_PATH, FR_BOLO_ATTACHMENTS_SUB_PATH } from '../../../router/constants';
import {
    MAX_NUM_OF_ATTACHMENTS_PER_BOLO,
    DEFAULT_ATTACHMENT_SORT_OPTION_VALUE,
} from '../../../attachments/constants';
import { AttachmentsHeaderActions } from '../../../attachments/attachments-header-actions';
import { AttachmentSortOptionValue } from '../../../attachments/types';
import { useAttachmentsAbilities } from '~/attachments/hooks/use-attachments-abilities';

export function FrBoloAttachmentsTab(): React.ReactElement | null {
    const { boloId } = useBoloIdFromNavigation();
    const navigate = useNavigate();
    const boloMetadata = useBoloPanelMetadata();
    const [attachmentCount, setAttachmentCount] = useState(0);
    const { canAddAttachments } = useAttachmentsAbilities('BOLO');
    const [sortOption, setSortOption] = useState<AttachmentSortOptionValue>(
        DEFAULT_ATTACHMENT_SORT_OPTION_VALUE
    );
    const attachmentsViewRef = useRef<AttachmentsViewHandle>(null);

    const handleAttachmentCountChange = useCallback((count: number) => {
        setAttachmentCount(count);
    }, []);

    const handleUploadClick = () => {
        attachmentsViewRef.current?.openFileDialog();
    };

    const isMaxReached = attachmentCount >= MAX_NUM_OF_ATTACHMENTS_PER_BOLO;

    if (!boloId) {
        return null;
    }

    const handleAttachmentClick = (attachmentId: number) => {
        navigate(
            `${FR_BOLO_ROOT_PATH}/${boloId}/${FR_BOLO_ATTACHMENTS_SUB_PATH}/${attachmentId}?sortOption=${sortOption}`
        );
    };

    return (
        <Box h="100%" display="flex" flexDirection="column">
            {canAddAttachments && attachmentCount > 0 && (
                <Box p={3} borderBottom="1px solid" borderColor="gray.200">
                    <HStack justifyContent="flex-end" align="center">
                        <AttachmentsHeaderActions
                            onUploadClick={handleUploadClick}
                            isUploadDisabled={isMaxReached}
                            sortOption={sortOption}
                            onSortChange={setSortOption}
                        />
                    </HStack>
                </Box>
            )}
            <Box flex={1} p={3} overflowY="auto">
                <AttachmentsView
                    ref={attachmentsViewRef}
                    entityId={boloId}
                    entityType="BOLO"
                    entityNumber={boloMetadata?.boloNumber}
                    onOpenPreview={handleAttachmentClick}
                    onAttachmentCountChange={handleAttachmentCountChange}
                    sortOption={sortOption}
                />
            </Box>
        </Box>
    );
}
